import React, { useRef } from "react";
import { Alert, AlertTitle, Button } from "@mui/material";

import { Progress } from "ui";

import { apiSlice, useGetVersionsQuery, versionsSlice } from "fond/api";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import useWorkflowErrorLogs from "fond/hooks/useWorkflowErrorLogs";
import { setVersion } from "fond/project/redux";
import { WorkflowKey } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { BlockSpinner } from "fond/widgets";

const stageConfig: Record<string, { label: string }> = {
  optimize: { label: "Optimizing build scope" },
  partition: { label: "Partitioning data" },
  prepare: { label: "Preparing inputs" },
  bom: { label: "Counting materials and costs" },
  c2s: { label: "Calculating cost to serve" },
};

interface DataModificationPanelProps {
  workflowKey: WorkflowKey;
  /**
   * Flag indicating if the editing is read only
   */
  isDisabled?: boolean;
}

const DataModificationPanel: React.FC<DataModificationPanelProps> = ({ workflowKey, isDisabled }) => {
  const dispatch = useAppDispatch();
  const versionId = useAppSelector((state) => state.project.versionId);
  const projectId = useAppSelector((state) => state.project.projectId);
  const { data: versionsData } = useGetVersionsQuery(projectId);
  const versionCountRef = useRef(versionsData?.ids?.length ?? 0);
  versionCountRef.current = versionsData?.ids?.length ?? 0;

  const { workflowState, stages, isLoading, logs, cancelWorkflow } = useCustomWorkflow({
    versionId,
    key: workflowKey,
    onComplete: async () => {
      const { getVersions } = versionsSlice.endpoints;
      const previousCount = versionCountRef.current;

      // Invalidate the Versions tag to ensure we fetch the latest versions
      dispatch(apiSlice.util.invalidateTags([{ type: "Versions", id: projectId }]));

      // Poll until a new version appears (the backend may not have it immediately)
      const maxAttempts = 10;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const subscription = dispatch(getVersions.initiate(projectId, { forceRefetch: true }));
        try {
          const versions = await subscription.unwrap();
          const ids = versions?.ids ?? [];
          // Wait until the version list has grown, then navigate to the newest
          if (ids.length > previousCount && ids[0]) {
            dispatch(setVersion(ids[0]));
            return;
          }
        } finally {
          subscription.unsubscribe();
        }

        await new Promise<void>((resolve) => {
          setTimeout(resolve, 1000);
        });
      }
    },
  });
  const { errorLogsInline, logsModal } = useWorkflowErrorLogs(logs);
  const isGeneratingCTS = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  if (isLoading) return <BlockSpinner />;

  return (
    <>
      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Cost to serve generation failed</AlertTitle>
          Cost to serve could not be generated.
          {errorLogsInline}
        </Alert>
      )}

      {stages && isGeneratingCTS && (
        <Alert sx={{ mb: 1 }} severity="info">
          <AlertTitle>Generating new scenario</AlertTitle>
        </Alert>
      )}

      {stages && (isGeneratingCTS || workflowState === "ERROR") && (
        <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: stageConfig[key]?.label ?? key, Status: status }))} />
      )}

      {isGeneratingCTS && (
        <Button fullWidth variant="contained" size="small" sx={{ mt: 1 }} disabled={!cancelWorkflow || isDisabled} onClick={() => cancelWorkflow?.()}>
          Cancel
        </Button>
      )}

      {logsModal}
    </>
  );
};

export default DataModificationPanel;
